import React from 'react';
import styled from 'styled-components';
import { useQuiz } from '../../context/QuizContext';
import QuizOption from './QuizOption';

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.background.paper};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.shadows.small};
  overflow: hidden;
  flex: 1;
  min-width: 0; /* Allow flexbox to shrink */
`;

const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.medium};
`;

const QuestionTitle = styled.h3`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const BookmarkButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  padding: 8px;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const QuestionContent = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md};
  overflow-y: auto;
`;

const QuestionText = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  line-height: 1.5;
`;

const OptionsContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const StatsContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.background.input};
  border-top: 1px solid ${({ theme }) => theme.colors.border.medium};
`;

const StatTitle = styled.h4`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const StatLabel = styled.span`
  margin-right: ${({ theme }) => theme.spacing.sm};
`;

const StatValue = styled.span`
  font-weight: bold;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border.medium};
`;

const ActionButton = styled.button<{ primary?: boolean }>`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-weight: bold;
  background-color: ${({ theme, primary }) => 
    primary ? theme.colors.secondary : theme.colors.background.input};
  color: ${({ theme }) => theme.colors.text.primary};
  
  &:hover {
    background-color: ${({ theme, primary }) => 
      primary ? theme.colors.tertiary : theme.colors.border.light};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuestionPanel: React.FC = () => {
  const { quiz, quizState, toggleMarkForLater, submitAnswer, resetQuiz } = useQuiz();
  
  if (!quiz) {
    return <PanelContainer>Loading...</PanelContainer>;
  }
  
  const handleToggleMarkForLater = () => {
    toggleMarkForLater();
  };
  
  const handleSubmitAnswer = () => {
    submitAnswer();
  };
  
  const handleTryAgain = () => {
    resetQuiz();
  };
  
  return (
    <PanelContainer>
      <QuestionHeader>
        <QuestionTitle>Question</QuestionTitle>
        
        <BookmarkButton onClick={handleToggleMarkForLater}>
          {quizState.isMarkedForLater ? '⭐' : '☆'}
        </BookmarkButton>
      </QuestionHeader>
      
      <QuestionContent>
        <QuestionText>{quiz.question.text}</QuestionText>
        
        <OptionsContainer>
          {quiz.options.map((option) => (
            <QuizOption key={option.id} option={option} />
          ))}
        </OptionsContainer>
      </QuestionContent>
      
      <StatsContainer>
        <StatTitle>Statistics</StatTitle>
        
        <StatRow>
          <StatItem>
            <StatLabel>Difficulty level:</StatLabel>
            <StatValue>{quiz.statistics.difficulty.level}</StatValue>
          </StatItem>
          
          <StatItem>
            <StatLabel>Peers who got it right:</StatLabel>
            <StatValue>
              {Math.round(quiz.statistics.difficulty.peers.score.correct / 
                quiz.statistics.difficulty.peers.score.total * 100)}%
            </StatValue>
          </StatItem>
          
          {quizState.isAnswered && (
            <StatItem>
              <StatLabel>Time taken:</StatLabel>
              <StatValue>{quizState.timeSpent}s</StatValue>
            </StatItem>
          )}
        </StatRow>
      </StatsContainer>
      
      <ActionButtonsContainer>
        {!quizState.isAnswered ? (
          <ActionButton 
            primary 
            onClick={handleSubmitAnswer}
            disabled={!quizState.selectedOption}
          >
            Submit Answer
          </ActionButton>
        ) : (
          <ActionButton onClick={handleTryAgain}>
            Try Again
          </ActionButton>
        )}
      </ActionButtonsContainer>
    </PanelContainer>
  );
};

export default QuestionPanel; 